// Playback + uptime analytics (Sprint 8). Contracts are FIXED and built
// concurrently by A1 — field names are camelCase here (mediaId, durationMs,
// uptimePct, perMedia, perScreen), deliberately unlike the snake_case rest of
// the API, because that is the agreed wire shape. Types live here (not in
// @seencast/shared yet) exactly like api/org.ts's scope types.

import { useQuery } from '@tanstack/react-query';
import { API_BASE, request } from './client.ts';
import { qk } from './keys.ts';

/** Whole org, one group, or a single screen. */
export type AnalyticsScopeType = 'org' | 'group' | 'screen';

export interface AnalyticsScope {
  scope_type: AnalyticsScopeType;
  /** Required for group/screen; omitted (undefined) for org-wide. */
  scope_id?: string;
}

/** Inclusive local-date range as `YYYY-MM-DD` strings (matches the CSV/API params). */
export interface AnalyticsRange {
  from: string;
  to: string;
}

export interface PlaybackTotals {
  plays: number;
  durationMs: number;
  /** null when the scope has no screens reporting uptime yet. */
  uptimePct: number | null;
}
export interface PlaybackPerMedia {
  mediaId: string;
  /** null = the media was deleted after it played (shown as "deleted media"). */
  name: string | null;
  plays: number;
  durationMs: number;
}
export interface PlaybackPerScreen {
  screenId: string;
  name: string;
  plays: number;
  durationMs: number;
  uptimePct: number | null;
}
export interface PlaybackReport {
  totals: PlaybackTotals;
  perMedia: PlaybackPerMedia[];
  perScreen: PlaybackPerScreen[];
}

export interface UptimeDay {
  date: string; // YYYY-MM-DD
  uptimePct: number | null;
}
export interface UptimePerScreen {
  screenId: string;
  name: string;
  days: UptimeDay[];
}
export interface UptimeReport {
  perScreen: UptimePerScreen[];
}

export type CsvMetric = 'playback' | 'uptime';

/** Inclusive list of YYYY-MM-DD dates spanning a range (UTC-based, capped at ~1yr).
 *  Used to align the sparse uptime series to the full selected period. */
export function enumerateDates(range: AnalyticsRange): string[] {
  const out: string[] = [];
  const d = new Date(`${range.from}T00:00:00Z`);
  const end = new Date(`${range.to}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(end.getTime()) || d > end) return [range.from];
  while (d <= end && out.length < 366) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out.length ? out : [range.from];
}

/**
 * Align a screen's uptime days to the full date range. The API returns days ONLY
 * for dates that recorded checks (the series is NOT zero-filled), so any date the
 * server omitted — or reported as null — becomes a `null` gap here. This keeps the
 * sparkline from interpolating across missing days or reading a gap as 0%.
 */
export function alignUptimeSeries(dates: string[], days: UptimeDay[]): Array<number | null> {
  const byDate = new Map(days.map((d) => [d.date, d.uptimePct]));
  return dates.map((date) => byDate.get(date) ?? null);
}

/** The four filter params every analytics endpoint (and the CSV export) shares. */
function scopeQuery(range: AnalyticsRange, scope: AnalyticsScope) {
  return {
    from: range.from,
    to: range.to,
    scope_type: scope.scope_type,
    // Only send scope_id when it's set — org-wide omits it entirely.
    ...(scope.scope_id ? { scope_id: scope.scope_id } : {}),
  };
}

export function getPlayback(range: AnalyticsRange, scope: AnalyticsScope): Promise<PlaybackReport> {
  return request<PlaybackReport>('/analytics/playback', { query: scopeQuery(range, scope) });
}

export function usePlayback(range: AnalyticsRange, scope: AnalyticsScope) {
  return useQuery({
    queryKey: qk.analyticsPlayback({ range, scope }),
    queryFn: () => getPlayback(range, scope),
  });
}

export function getUptime(range: AnalyticsRange, scope: AnalyticsScope): Promise<UptimeReport> {
  return request<UptimeReport>('/analytics/uptime', { query: scopeQuery(range, scope) });
}

export function useUptime(range: AnalyticsRange, scope: AnalyticsScope) {
  return useQuery({
    queryKey: qk.analyticsUptime({ range, scope }),
    queryFn: () => getUptime(range, scope),
  });
}

/**
 * Absolute URL for the CSV export with the current filters, for an `<a download>`.
 * A plain link can't carry the `X-Org-Id` header, so the API falls back to the
 * session org (fine for single-org users / demo mode). Pure + exported so the
 * unit test can assert the query is composed correctly.
 */
export function exportCsvUrl(range: AnalyticsRange, scope: AnalyticsScope, metric: CsvMetric): string {
  const params = new URLSearchParams();
  params.set('from', range.from);
  params.set('to', range.to);
  params.set('scope_type', scope.scope_type);
  if (scope.scope_id) params.set('scope_id', scope.scope_id);
  params.set('metric', metric);
  return `${API_BASE}/analytics/export.csv?${params.toString()}`;
}
